// src/hooks/rq/useToggleLike.js

import { useMutation, useQueryClient } from '@tanstack/react-query';
import API from '../../api/client';
import { fetchPetById } from '../../api/queries';

const toggleLikeApi = async ({ petId }) => {
  const response = await API.post(`/pets/${petId}/like/`);
  return {
    petId,
    liked: response.data.liked,
    like_count: response.data.like_count,
  };
};

export function useToggleLikeRQ(location) {
  const queryClient = useQueryClient();
  const latKey = location.lat ?? 'none';
  const lngKey = location.lng ?? 'none';
  const favKey = ['favorites', latKey, lngKey];

  return useMutation({
    mutationFn: toggleLikeApi,

    onMutate: async ({ petId }) => {
      console.log(
        '[useToggleLikeRQ] onMutate para petId=',
        petId,
        'location=',
        location
      );
      // 1) Cancelamos cualquier refetch en favorites
      await queryClient.cancelQueries({ queryKey: favKey });

      // 2) Guardamos snapshots previos para revertir en caso de error
      const previousFavs = queryClient.getQueryData(favKey);
      const previousPet  = queryClient.getQueryData(['pet', petId]);

      // 3) Optimistic update: actualizar lista de favoritos
      queryClient.setQueryData(favKey, old => {
        if (!old) return old;
        return old.map(p => {
          if (p.id !== petId) return p;
          const liked = !p.liked_by_user;
          return {
            ...p,
            liked_by_user: liked,
            like_count:    p.like_count + (liked ? 1 : -1),
          };
        });
      });

      // 4) Optimistic update: actualizar cache de detalle de pet
      queryClient.setQueryData(['pet', petId], pet => {
        if (!pet) return pet;
        const liked = !pet.liked_by_user;
        return {
          ...pet,
          liked_by_user: liked,
          like_count:    pet.like_count + (liked ? 1 : -1),
        };
      });

      // 5) Optimistic update: también sobre el feed infinito de pets,
      //    para que el corazón palpite al instante en HomePage/PetCard
      queryClient.setQueriesData(
        { queryKey: ['pets'], exact: false },
        old => {
          if (!old) return old;
          // infiniteQuery shape: { pages: [...], pageParams: [...] }
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              results: page.results.map(p => {
                if (p.id !== petId) return p;
                const liked = !p.liked_by_user;
                return {
                  ...p,
                  liked_by_user: liked,
                  like_count:    p.like_count + (liked ? 1 : -1),
                };
              })
            }))
          };
        }
      );

      return { previousFavs, previousPet };
    },

    onSuccess: async ({ petId, liked }) => {
      // Si acabamos de marcar como favorito y no estaba en la lista, lo insertamos
      if (liked) {
        // Obtener detalle desde caché o desde servidor
        const petDetail =
          queryClient.getQueryData(['pet', petId]) ||
          (await queryClient.fetchQuery(['pet', petId], () => fetchPetById(petId)));

        queryClient.setQueryData(favKey, old => {
          const list = Array.isArray(old) ? old : [];
          // Evitar duplicados
          if (!list.find(p => p.id === petDetail.id)) {
            return [...list, petDetail];
          }
          return list;
        });
      }
    },

    onError: (_err, vars, context) => {
      // Revertir cambios si la mutación falla
      queryClient.setQueryData(favKey, context.previousFavs);
      queryClient.setQueryData(['pet', vars.petId], context.previousPet);
    },

    onSettled: (_data, _error, vars) => {
      const petKey = ['pet', vars.petId];
      // 1) Invalidar solo favoritos (para recargar del servidor)
      queryClient.invalidateQueries({ queryKey: favKey, exact: true });
      // 2) Invalidar detalle de pet
      queryClient.invalidateQueries({ queryKey: petKey, exact: true });
      // → Ya no invalidamos el feed de pets para evitar el refetch completo
    },
  });
}
